import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterSidebar = ({ isOpen, onClose, filters, onFilterChange }) => {
  const [priceRange, setPriceRange] = useState([0, 500]);

  const categories = [
    { id: 'shoes', label: 'Shoes', count: 156 },
    { id: 'clothes', label: 'Clothes', count: 89 },
    { id: 'bags', label: 'Bags', count: 67 }
  ];

  const brands = [
    { id: 'nike', label: 'Nike', count: 45 },
    { id: 'adidas', label: 'Adidas', count: 38 },
    { id: 'puma', label: 'Puma', count: 29 },
    { id: 'reebok', label: 'Reebok', count: 22 },
    { id: 'converse', label: 'Converse', count: 18 }
  ];

  const sizes = [
    { id: 'xs', label: 'XS' },
    { id: 's', label: 'S' },
    { id: 'm', label: 'M' },
    { id: 'l', label: 'L' },
    { id: 'xl', label: 'XL' },
    { id: 'xxl', label: 'XXL' }
  ];

  const colors = [
    { id: 'black', label: 'Black', color: '#000000' },
    { id: 'white', label: 'White', color: '#FFFFFF' },
    { id: 'brown', label: 'Brown', color: '#8B4513' },
    { id: 'blue', label: 'Blue', color: '#0066CC' },
    { id: 'red', label: 'Red', color: '#CC0000' },
    { id: 'green', label: 'Green', color: '#00CC00' }
  ];

  const handleCategoryChange = (categoryId, checked) => {
    const updatedCategories = checked
      ? [...(filters?.categories || []), categoryId]
      : (filters?.categories || [])?.filter(id => id !== categoryId);
    onFilterChange({ ...filters, categories: updatedCategories });
  };

  const handleBrandChange = (brandId, checked) => {
    const updatedBrands = checked
      ? [...(filters?.brands || []), brandId]
      : (filters?.brands || [])?.filter(id => id !== brandId);
    onFilterChange({ ...filters, brands: updatedBrands });
  };

  const clearAllFilters = () => {
    onFilterChange({});
    setPriceRange([0, 500]);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      {/* Sidebar */}
      <div className={`
        fixed lg:sticky top-0 left-0 h-full lg:h-auto w-80 lg:w-64 bg-background border-r border-border z-50 lg:z-auto
        transform transition-transform duration-300 ease-in-out overflow-y-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-foreground">Filters</h2>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                Clear All
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="lg:hidden"
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          {/* Categories */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-3">Categories</h3>
            <div className="space-y-2">
              {categories?.map((category) => (
                <div key={category?.id} className="flex items-center justify-between">
                  <Checkbox
                    label={category?.label}
                    checked={(filters?.categories || [])?.includes(category?.id)}
                    onChange={(e) => handleCategoryChange(category?.id, e?.target?.checked)}
                  />
                  <span className="text-xs text-muted-foreground">
                    {category?.count}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Price Range */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-3">Price Range</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">$0</span>
                <span className="text-muted-foreground">$500+</span>
              </div>
              <input
                type="range"
                min="0"
                max="500"
                value={priceRange?.[1]}
                onChange={(e) => setPriceRange([0, parseInt(e?.target?.value)])}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              />
              <div className="text-center">
                <span className="text-sm font-medium text-foreground">
                  $0 - ${priceRange?.[1]}
                </span>
              </div>
            </div>
          </div>

          {/* Brands */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-3">Brands</h3>
            <div className="space-y-2">
              {brands?.map((brand) => (
                <div key={brand?.id} className="flex items-center justify-between">
                  <Checkbox
                    label={brand?.label}
                    checked={(filters?.brands || [])?.includes(brand?.id)}
                    onChange={(e) => handleBrandChange(brand?.id, e?.target?.checked)}
                  />
                  <span className="text-xs text-muted-foreground">
                    {brand?.count}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Sizes */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-3">Sizes</h3>
            <div className="grid grid-cols-3 gap-2">
              {sizes?.map((size) => (
                <button
                  key={size?.id}
                  className={`
                    px-3 py-2 text-sm border border-border rounded-md transition-colors
                    ${(filters?.sizes || [])?.includes(size?.id)
                      ? 'bg-accent text-accent-foreground border-accent'
                      : 'bg-background text-foreground hover:bg-muted'
                    }
                  `}
                  onClick={() => {
                    const updatedSizes = (filters?.sizes || [])?.includes(size?.id)
                      ? (filters?.sizes || [])?.filter(id => id !== size?.id)
                      : [...(filters?.sizes || []), size?.id];
                    onFilterChange({ ...filters, sizes: updatedSizes });
                  }}
                >
                  {size?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Colors */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-3">Colors</h3>
            <div className="grid grid-cols-6 gap-2">
              {colors?.map((color) => (
                <button
                  key={color?.id}
                  className={`
                    w-8 h-8 rounded-full border-2 transition-all
                    ${(filters?.colors || [])?.includes(color?.id)
                      ? 'border-accent scale-110' :'border-border hover:border-muted-foreground'
                    }
                  `}
                  style={{ backgroundColor: color?.color }}
                  onClick={() => {
                    const updatedColors = (filters?.colors || [])?.includes(color?.id)
                      ? (filters?.colors || [])?.filter(id => id !== color?.id)
                      : [...(filters?.colors || []), color?.id];
                    onFilterChange({ ...filters, colors: updatedColors });
                  }}
                  title={color?.label}
                />
              ))}
            </div>
          </div>

          {/* Rating */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-3">Rating</h3>
            <div className="space-y-2">
              {[4, 3, 2, 1]?.map((rating) => (
                <button
                  key={rating}
                  className={`
                    flex items-center space-x-2 w-full text-left p-2 rounded-md transition-colors
                    ${filters?.rating === rating
                      ? 'bg-accent/10 text-accent' :'hover:bg-muted text-muted-foreground'
                    }
                  `}
                  onClick={() => {
                    onFilterChange({ 
                      ...filters, 
                      rating: filters?.rating === rating ? null : rating 
                    });
                  }}
                >
                  <div className="flex items-center">
                    {[...Array(5)]?.map((_, i) => (
                      <Icon
                        key={i}
                        name="Star"
                        size={14}
                        className={`${
                          i < rating ? 'text-warning fill-current' : 'text-muted-foreground'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm">& Up</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;